const aliases: Record<string, string> = {
  "Europe/Prague": "Praha Prague Brno Ostrava",
  "Europe/Bratislava": "Bratislava Košice",
  "Europe/London": "Londýn London",
  "Europe/Vienna": "Vídeň Vienna Wien",
  "Europe/Berlin": "Berlín Berlin",
  "Europe/Paris": "Paříž Paris",
  "Europe/Warsaw": "Varšava Warsaw Warszawa",
  "Europe/Rome": "Řím Rome Roma",
  "Europe/Budapest": "Budapešť Budapest",
  "America/New_York": "New York",
  "America/Los_Angeles": "Los Angeles",
  "Asia/Tokyo": "Tokio Tokyo",
};
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[_\s]+/g, " ")
    .trim();

const offsetMinutes = (value: string): number | undefined => {
  const match = /(?:utc|gmt)\s*([+-])(\d{1,2})(?::?(\d{2}))?/i.exec(value);
  return match
    ? (Number(match[2]) * 60 + Number(match[3] ?? 0)) *
        (match[1] === "-" ? -1 : 1)
    : /^(utc|gmt)$/i.test(value.trim())
      ? 0
      : undefined;
};

export function mountTimezonePicker(
  root: HTMLElement,
  options: {
    current: string;
    zones: string[];
    locale: string;
    text?: (
      key: string,
      fallback: string,
      values?: Record<string, string>,
    ) => string;
    label: (zone: string) => string;
    onSelect: (zone: string) => void;
  },
): void {
  const cs = options.locale === "cs";
  const text = (
    key: string,
    cz: string,
    en: string,
    values: Record<string, string> = {},
  ) => {
    let value = options.text?.(key, cs ? cz : en, values) ?? (cs ? cz : en);
    for (const [name, replacement] of Object.entries(values))
      value = value.replaceAll(`{${name}}`, replacement);
    return value;
  };
  const label = document.createElement("label");
  label.htmlFor = "timezone";
  label.textContent = text("form.timezone", "Časové pásmo", "Time zone");
  const input = document.createElement("input");
  input.id = "timezone";
  input.type = "text";
  input.autocomplete = "off";
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", "timezone-options");
  input.setAttribute("aria-expanded", "false");
  input.placeholder = text(
    "timezone.search",
    "Město nebo časové pásmo",
    "City or time zone",
  );
  input.value = options.current.replaceAll("_", " ");
  const list = document.createElement("div");
  list.id = "timezone-options";
  list.role = "listbox";
  list.hidden = true;
  list.setAttribute("aria-label", label.textContent);
  const status = document.createElement("small");
  status.setAttribute("aria-live", "polite");
  status.textContent = options.label(options.current);
  root.replaceChildren(label, input, list, status);
  const zones = [...new Set([options.current, "UTC", ...options.zones])];
  let matches = zones;
  let active = -1;
  const close = () => {
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    input.value = options.current.replaceAll("_", " ");
  };
  const choose = (zone: string) => {
    options.current = zone;
    close();
    status.textContent = options.label(zone);
    options.onSelect(zone);
  };
  const render = (query: string) => {
    const q = normalize(query);
    const offset = offsetMinutes(q);
    matches = zones.filter(
      (zone) =>
        normalize(zone + " " + (aliases[zone] ?? "")).includes(q) ||
        (/^(utc|gmt)/.test(q) &&
          (normalize(options.label(zone)).includes(q) ||
            (offset !== undefined &&
              offsetMinutes(options.label(zone)) === offset))),
    );
    active = -1;
    input.removeAttribute("aria-activedescendant");
    list.replaceChildren();
    matches.forEach((zone, index) => {
      const item = document.createElement("div");
      item.id = "timezone-option-" + index;
      item.role = "option";
      item.setAttribute("aria-selected", String(zone === options.current));
      item.textContent = options.label(zone);
      item.addEventListener("mousedown", (event) => event.preventDefault());
      item.addEventListener("click", () => choose(zone));
      list.append(item);
    });
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    status.textContent = matches.length
      ? text(
          "timezone.results",
          "{count} pásem v nabídce",
          "{count} time zones",
          { count: String(matches.length) },
        )
      : text(
          "timezone.noResults",
          "Žádné odpovídající pásmo",
          "No matching time zones",
        );
  };
  input.addEventListener("focus", () => {
    input.select();
  });
  input.addEventListener("click", () => {
    if (list.hidden) render("");
  });
  input.addEventListener("input", () => render(input.value));
  input.addEventListener("blur", () => {
    close();
    status.textContent = options.label(options.current);
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !list.hidden) {
      event.preventDefault();
      close();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (list.hidden) render("");
      if (!matches.length) return;
      active =
        active < 0
          ? event.key === "ArrowDown"
            ? 0
            : matches.length - 1
          : (active + (event.key === "ArrowDown" ? 1 : -1) + matches.length) %
            matches.length;
      [...list.children].forEach((item, i) =>
        item.setAttribute("aria-selected", String(i === active)),
      );
      const item = list.children[active] as HTMLElement;
      input.setAttribute("aria-activedescendant", item.id);
      item.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
    if (event.key === "Enter" && !list.hidden) {
      event.preventDefault();
      const zone =
        matches[active] ?? (matches.length === 1 ? matches[0] : undefined);
      if (zone) choose(zone);
    }
  });
}
